import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Upload, Video, FileVideo, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminVideos = () => {
  const { token } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const loadVideos = async () => {
    try {
      const res = await axios.get(`${API}/admin/videos`, { headers: { Authorization: `Bearer ${token}` } });
      setVideos(res.data.videos || []);
    } catch {
      toast.error("Failed to load videos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadVideos(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(mp4|webm)$/i.test(file.name)) {
      toast.error("Only .mp4 or .webm files allowed");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
    setProgress(0);
    try {
      await axios.post(`${API}/admin/upload-video`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      toast.success(`Uploaded: ${file.name}`);
      await loadVideos();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/admin/dashboard" data-testid="back-to-dashboard">
            <Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-2" />Dashboard</Button>
          </Link>
          <h1 className="text-xl font-heading font-bold flex items-center gap-2">
            <Video className="w-5 h-5" />Video Library
          </h1>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Upload New Video</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Upload <code>.mp4</code> or <code>.webm</code> files. Once uploaded, videos are auto-cached by the Service Worker for offline playback. Reference them in protocol info_boxes via <code>/videos/&lt;filename&gt;</code>.
            </p>
            <input
              ref={fileRef}
              type="file"
              accept="video/mp4,video/webm"
              onChange={handleUpload}
              disabled={uploading}
              className="hidden"
              data-testid="video-file-input"
            />
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="w-full"
              data-testid="upload-video-btn"
            >
              {uploading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading {progress}%</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" />Choose Video File</>
              )}
            </Button>
            {uploading && (
              <div className="w-full bg-secondary rounded-full h-2 overflow-hidden">
                <div className="bg-primary h-full transition-all" style={{ width: `${progress}%` }} />
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Library ({videos.length})</CardTitle>
            <Button variant="outline" size="sm" onClick={loadVideos} disabled={loading}>Refresh</Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : videos.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">No videos uploaded yet.</div>
            ) : (
              <div className="space-y-2" data-testid="video-list">
                {videos.map((v) => (
                  <div
                    key={v.filename}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-secondary/30 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <FileVideo className="w-5 h-5 text-primary flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium font-mono text-sm truncate">{v.filename}</div>
                        <div className="text-xs text-muted-foreground">
                          <Badge variant="outline" className="mr-2">{v.size_mb} MB</Badge>
                          <code>{v.path}</code>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(v.path, "_blank")}
                      >
                        Preview
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(v.path);
                          toast.success("Path copied");
                        }}
                      >
                        Copy Path
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminVideos;
